import { ReactNode, memo } from 'react';
import clsx from 'clsx';
import { ComponentMemo } from '@headless-workspace/typings';
import {
    GlowClickable,
    GlowImage,
    GlowPrice,
    GlowTextBody,
    GlowTextBodySmall,
    PropsWithImageTagName,
    PropsWithProduct,
} from '../../../../src';

type GlowBasketProductPlotProps = PropsWithImageTagName &
    PropsWithProduct & {
        textQuantityLabel?: string;
        discountPercentageLabel?: string;
        fullWidth?: boolean;
        classNames?: {
            container?: string;
            mainContent?: string;
            extraContent?: string;
        };
        VATLegalMentionContent?: ReactNode;
    };

const IMAGE_SIZE = 80;

/**
 * Component called plotProduit_cart on FIGMA
 */

export const GlowBasketProductPlot: ComponentMemo<GlowBasketProductPlotProps> = memo(
    ({
        priceAlwaysBlack,
        enableDiscountInfo,
        isArialFont,
        image,
        brand,
        price,
        priceBeforeDiscount,
        pricePerUnit,
        name,
        description,
        href,
        discountPercentageLabel,
        textQuantityLabel,
        ImageTagName,
        classNames,
        fullWidth = false,
        VATLegalMentionContent,
    }) => {
        const fontFamily = isArialFont ? 'arial' : 'brand';

        return (
            <div className={clsx('flex flex-row items-start gap-1 py-0.5', classNames?.container)}>
                <GlowClickable
                    href={href}
                    content={{
                        icon: (
                            <GlowImage
                                image={image}
                                className={'w-basketProductImg h-basketProductImg'}
                                width={IMAGE_SIZE}
                                height={IMAGE_SIZE}
                                TagName={ImageTagName}
                            />
                        ),
                    }}
                />
                <div className={'flex flex-col gap-0.5 w-full'}>
                    <div className={clsx('flex justify-between gap-x-0.5', classNames?.mainContent)}>
                        <GlowClickable
                            className={'!items-start'}
                            href={href}
                            content={{
                                labelElement: (
                                    <div className={'flex flex-col'}>
                                        <GlowTextBody
                                            fontWeight={'bold'}
                                            text={brand.name}
                                            fontFamily={fontFamily}
                                            className={clsx('line-clamp-1', {
                                                'max-w-basketProductInformation': !fullWidth,
                                            })}
                                        />
                                        <GlowTextBodySmall
                                            text={name}
                                            fontFamily={fontFamily}
                                            className={clsx('line-clamp-2', {
                                                'max-w-basketProductInformation': !fullWidth,
                                            })}
                                        />
                                    </div>
                                ),
                            }}
                        />
                        <GlowPrice
                            price={price}
                            pricePerUnit={pricePerUnit}
                            priceBeforeDiscount={priceBeforeDiscount}
                            discountPercentageLabel={discountPercentageLabel}
                            priceAlwaysBlack={priceAlwaysBlack}
                            enableDiscountInfo={enableDiscountInfo}
                            fontFamily={fontFamily}
                        />
                    </div>
                    <div className={clsx('flex flex-col gap-0.25', classNames?.extraContent)}>
                        <GlowTextBodySmall
                            color={'tertiary'}
                            text={description}
                            fontFamily={fontFamily}
                            className={'line-clamp-1 max-w-basketProductInformation'}
                        />
                        <GlowTextBodySmall text={textQuantityLabel} fontFamily={fontFamily} />

                        {VATLegalMentionContent}
                    </div>
                </div>
            </div>
        );
    },
);

GlowBasketProductPlot.displayName = 'GlowBasketProductPlot';
